import React, { useState, useEffect } from 'react';
import styles from './Admin.module.css';

const EventList = ({ registrations, updateEventRegistrationVerification }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'verified', 'unverified', 'pending'
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedCongress, setSelectedCongress] = useState('all');
    const [localRegistrations, setLocalRegistrations] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('all');
    const [selectedProfession, setSelectedProfession] = useState('all');

    // Synchroniser les inscriptions locales avec les props
    useEffect(() => {
        console.log("Inscriptions reçues:", registrations);
        if (registrations && registrations.length > 0) {
            console.log("Exemple d'inscription:", registrations[0]);
            console.log("Type de event_verified:", typeof registrations[0].event_verified);
        }
        setLocalRegistrations(registrations);
    }, [registrations]);

    // Extraire la liste unique des congrès
    const congressList = Array.from(new Set(localRegistrations.map(reg => reg.congress_id)))
        .map(id => {
            const registration = localRegistrations.find(reg => reg.congress_id === id);
            return {
                id,
                name: registration?.congress_name || `Congrès #${id}`
            };
        });

    // Extraire la liste unique des pays
    const countryList = Array.from(
        new Set(localRegistrations.filter(reg => reg.country_name).map(reg => reg.country_name))
    ).sort();

    // Extraire la liste unique des professions
    const professionList = Array.from(
        new Set(localRegistrations.filter(reg => reg.profession_name).map(reg => reg.profession_name))
    ).sort();

    // Filtrer les inscriptions selon les critères
    const filteredRegistrations = localRegistrations.filter(registration => {
        // Log pour débogage
        console.log(`Registration ${registration.id} (${registration.first_name || registration.email}): event_verified = ${JSON.stringify(registration.event_verified)}`);

        // Filtre de recherche
        const searchMatch =
            (registration.first_name && registration.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (registration.last_name && registration.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (registration.email && registration.email.toLowerCase().includes(searchTerm.toLowerCase()));

        // Filtre de statut
        let statusMatch = true;
        if (filter === 'verified') {
            statusMatch = registration.event_verified === true;
        } else if (filter === 'unverified') {
            statusMatch = registration.event_verified === false;
        } else if (filter === 'pending') {
            statusMatch = registration.event_verified === null;
        }

        // Filtre de congrès
        let congressMatch = true;
        if (selectedCongress !== 'all') {
            congressMatch = registration.congress_id === parseInt(selectedCongress);
        }

        // Filtre de pays
        let countryMatch = true;
        if (selectedCountry !== 'all') {
            countryMatch = registration.country_name === selectedCountry;
        }

        // Filtre de profession
        let professionMatch = true;
        if (selectedProfession !== 'all') {
            professionMatch = registration.profession_name === selectedProfession;
        }

        return searchMatch && statusMatch && congressMatch && countryMatch && professionMatch;
    });

    // Fonction pour déterminer le statut d'affichage
    const getDisplayStatus = (status) => {
        if (status === true) return 'Vérifié';
        if (status === false) return 'Non vérifié';
        return 'En attente';
    };

    // Fonction pour gérer la mise à jour du statut
    const handleUpdateStatus = (registrationId, isVerified) => {
        console.log(`Mise à jour du statut pour l'inscription ${registrationId} à ${isVerified}`);

        // Mettre à jour localement avant l'appel API
        setLocalRegistrations(prevRegistrations =>
            prevRegistrations.map(registration =>
                registration.id === registrationId
                    ? { ...registration, event_verified: isVerified }
                    : registration
            )
        );

        // Appeler la fonction de mise à jour du parent
        updateEventRegistrationVerification(registrationId, isVerified);
    };

    // Fonction pour exporter les inscriptions filtrées en CSV
    const exportToCSV = () => {
        // Définir les en-têtes du CSV
        const headers = [
            'ID',
            'Nom',
            'Prénom',
            'Email',
            'Événement',
            'Date de participation',
            'Contact Stand',
            'Pays',
            'Profession',
            'Statut'
        ];

        // Transformer les données des inscriptions en lignes CSV
        const csvRows = filteredRegistrations.map(registration => [
            registration.id,
            registration.last_name || '',
            registration.first_name || '',
            registration.email || '',
            registration.congress_name || `Congrès #${registration.congress_id}` || '',
            registration.attendance_date ? new Date(registration.attendance_date).toLocaleDateString() : '',
            registration.contact_booth ? 'Oui' : 'Non',
            registration.country_name || '',
            registration.profession_name || '',
            getDisplayStatus(registration.event_verified)
        ]);

        // Ajouter les en-têtes au début
        csvRows.unshift(headers);

        // Convertir chaque ligne en chaîne CSV et les joindre avec des sauts de ligne
        const csvContent = csvRows
            .map(row =>
                row
                    .map(cell =>
                        // Échapper les guillemets et entourer de guillemets si nécessaire
                        typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))
                            ? `"${cell.replace(/"/g, '""')}"`
                            : cell
                    )
                    .join(',')
            )
            .join('\n');

        // Créer un Blob avec le contenu CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Créer un URL pour le Blob
        const url = URL.createObjectURL(blob);

        // Créer un lien pour télécharger le fichier
        const link = document.createElement('a');
        link.setAttribute('href', url);

        // Définir le nom du fichier avec la date actuelle
        const date = new Date().toISOString().split('T')[0];
        link.setAttribute('download', `inscriptions_evenements_sav-iol_${date}.csv`);

        // Ajouter le lien au document, cliquer dessus, puis le supprimer
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={styles.userListContainer}>
            <div className={styles.filterControls}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Rechercher une inscription..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>

                <div className={styles.filterButtons}>
                    <button
                        className={`${styles.filterButton} ${filter === 'all' ? styles.active : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        Tous
                    </button>
                    <button
                        className={`${styles.filterButton} ${filter === 'verified' ? styles.active : ''}`}
                        onClick={() => setFilter('verified')}
                    >
                        Vérifiés
                    </button>
                    <button
                        className={`${styles.filterButton} ${filter === 'unverified' ? styles.active : ''}`}
                        onClick={() => setFilter('unverified')}
                    >
                        Non vérifiés
                    </button>
                    <button
                        className={`${styles.filterButton} ${filter === 'pending' ? styles.active : ''}`}
                        onClick={() => setFilter('pending')}
                    >
                        En attente
                    </button>
                </div>

                <div className={styles.advancedFilters}>
                    <div className={styles.filterSelect}>
                        <label>Événement:</label>
                        <select
                            value={selectedCongress}
                            onChange={(e) => setSelectedCongress(e.target.value)}
                            className={styles.filterSelectInput}
                        >
                            <option value="all">Tous les événements</option>
                            {congressList.map(congress => (
                                <option key={congress.id} value={congress.id}>
                                    {congress.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.filterSelect}>
                        <label>Pays:</label>
                        <select
                            value={selectedCountry}
                            onChange={(e) => setSelectedCountry(e.target.value)}
                            className={styles.filterSelectInput}
                        >
                            <option value="all">Tous les pays</option>
                            {countryList.map(country => (
                                <option key={country} value={country}>
                                    {country}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.filterSelect}>
                        <label>Profession:</label>
                        <select
                            value={selectedProfession}
                            onChange={(e) => setSelectedProfession(e.target.value)}
                            className={styles.filterSelectInput}
                        >
                            <option value="all">Toutes les professions</option>
                            {professionList.map(profession => (
                                <option key={profession} value={profession}>
                                    {profession}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className={styles.listActions}>
                <div className={styles.userCount}>
                    {filteredRegistrations.length} inscription(s) trouvée(s)
                </div>
                <button
                    className={styles.exportButton}
                    onClick={exportToCSV}
                    disabled={filteredRegistrations.length === 0}
                >
                    Exporter en CSV
                </button>
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.userTable}>
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Prénom</th>
                            <th>Email</th>
                            <th>Événement</th>
                            <th>Date</th>
                            <th>Contact Stand</th>
                            <th>Statut</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRegistrations.length > 0 ? (
                            filteredRegistrations.map(registration => (
                                <tr
                                    key={registration.id}
                                    className={
                                        registration.event_verified === true
                                            ? styles.verifiedUser
                                            : registration.event_verified === false
                                                ? styles.unverifiedUser
                                                : styles.pendingUser
                                    }
                                >
                                    <td>{registration.last_name || '-'}</td>
                                    <td>{registration.first_name || '-'}</td>
                                    <td>{registration.email || '-'}</td>
                                    <td>{registration.congress_name || `Congrès #${registration.congress_id}` || '-'}</td>
                                    <td>{registration.attendance_date ? new Date(registration.attendance_date).toLocaleDateString() : '-'}</td>
                                    <td>{registration.contact_booth ? 'Oui' : 'Non'}</td>
                                    <td>
                                        {getDisplayStatus(registration.event_verified)}
                                    </td>
                                    <td className={styles.actionButtons}>
                                        <button
                                            className={`${styles.verifyButton} ${registration.event_verified === true ? styles.active : ''}`}
                                            onClick={() => handleUpdateStatus(registration.id, true)}
                                            disabled={registration.event_verified === true}
                                        >
                                            Approuver
                                        </button>
                                        <button
                                            className={`${styles.rejectButton} ${registration.event_verified === false ? styles.active : ''}`}
                                            onClick={() => handleUpdateStatus(registration.id, false)}
                                            disabled={registration.event_verified === false}
                                        >
                                            Rejeter
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className={styles.noResults}>
                                    Aucune inscription trouvée
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EventList; 